/**
 * Document-wide "grabbing" cursor for the duration of a slider drag, shared by
 * range-slider and zoom-slider.
 *
 * This has to be a document-level rule rather than something a shadow root can
 * style: once a drag starts, the pointer roams over the whole page, and every
 * element it crosses would otherwise assert its own cursor — the track's
 * `pointer`, the thumb's `grab`, a button's `pointer` — so the cursor flickers
 * while you drag. Overriding every element is the point of the `*` selector and
 * the `!important`, and it is why setting `document.body.style.cursor` is not a
 * substitute: descendants with their own `cursor` win over an inherited one.
 *
 * The rule lives here, in a stylesheet the sliders adopt while dragging, rather
 * than sitting permanently in main.css. Same effect, but the components own it
 * and nothing global has to exist for them to work.
 */

const sheet = new CSSStyleSheet();
sheet.replaceSync(`*, *::before, *::after { cursor: grabbing !important; }`);

/** Drags in flight. Counted because the menu drawer stacks several sliders and
 *  two fingers can hold two of them at once — the cursor must survive until the
 *  last one lets go. */
let activeDrags = 0;

/** Force the grabbing cursor document-wide. Pair with `endSliderDragCursor`. */
export function beginSliderDragCursor(): void {
  activeDrags += 1;
  if (activeDrags === 1) {
    document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
  }
}

/** Release one drag's claim on the cursor, restoring it when none are left.
 *  Safe to call for a drag that never began — the count floors at zero, so a
 *  teardown path that runs twice can't strand the cursor. */
export function endSliderDragCursor(): void {
  if (activeDrags === 0) return;
  activeDrags -= 1;
  if (activeDrags === 0) {
    document.adoptedStyleSheets = document.adoptedStyleSheets.filter((s) => s !== sheet);
  }
}
